import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faCogs,
  faUsers,
  faBook,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import PurchaseModal from './PurchaseModal';
import DemoModal from './DemoModal';

// 导入页面组件
import Home from '../pages/Home';
import Products from '../pages/Products';
import Cases from '../pages/cases';
import Resources from '../pages/resources';
import About from '../pages/About';

// 导入品牌配置和SEO组件
import { brandConfig } from '../config/brandconfig';
import SEOHead from './seohead';

const MainLayout = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('home');
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const headerRef = useRef(null);

  const tabs = [
    {
      id: 'home',
      label: '首页',
      icon: faHome,
      component: Home
    },
    {
      id: 'products',
      label: '产品功能',
      icon: faCogs,
      component: Products
    },
    {
      id: 'cases',
      label: '客户案例',
      icon: faUsers,
      component: Cases
    },
    // {
    //   id: 'resources',
    //   label: '价格方案',
    //   icon: faBook,
    //   component: Resources
    // },
    {
      id: 'about',
      label: '关于我们',
      icon: faInfoCircle,
      component: About
    }
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || Home;

  // 处理滚动事件
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // 设置滚动状态，用于改变导航栏背景
      setIsScrolled(currentScrollY > 50);
      
      // 向下滚动且滚动距离超过100px时隐藏导航栏
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
      }
      // 向上滚动时显示导航栏
      else if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    // 处理鼠标移动事件
    const handleMouseMove = (e) => {
      // 当鼠标移动到顶部50px范围内时显示导航栏
      if (e.clientY <= 50) {
        setIsHeaderVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [lastScrollY]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 动态SEO头部信息 */}
      <SEOHead />
      {/* 顶部导航栏 */}
      <header 
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out ${
          isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
        } ${
          isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200' : 'bg-black/10 backdrop-blur-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* 左侧Logo */}
            <div className="flex items-center">
              <img 
                src={isScrolled 
                  ? `/logo-dark.png?v=${Date.now()}`
                  : `/logo-white.png?v=${Date.now()}`
                } 
                alt={`${brandConfig.brand.name}${brandConfig.brand.slogan}`} 
                className="h-12 w-auto transition-all duration-300"
              />
            </div>

            {/* 居中标签导航 */}
            <nav className="flex space-x-6 absolute left-1/2 transform -translate-x-1/2" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? (isScrolled ? 'text-blue-600 border-b-2 border-blue-500' : 'text-white border-b-2 border-white shadow-lg')
                      : (isScrolled ? 'text-gray-500 hover:text-gray-700' : 'text-white hover:text-white/90')
                  } whitespace-nowrap py-2 px-1 font-medium text-sm flex items-center space-x-2 transition-all duration-300 ${
                    !isScrolled ? 'drop-shadow-lg text-shadow-lg' : ''
                  }`}
                  style={!isScrolled ? {
                    textShadow: '0 2px 4px rgba(0,0,0,0.8), 0 1px 2px rgba(0,0,0,0.6)'
                  } : {}}
                >
                  <FontAwesomeIcon icon={tab.icon} className="text-sm" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
            
            {/* 右侧空白区域 */}
            <div className="flex items-center">
              {/* 移除免费试用按钮 */}
            </div>
          </div>
        </div>
      </header>

      {/* 主要内容区域 */}
      <main className="flex-1">
        <ActiveComponent 
          onDemoClick={() => setIsDemoModalOpen(true)}
          onPurchaseClick={() => setIsPurchaseModalOpen(true)}
        />
      </main>

      {/* 页脚 - 集团其他业务链接 */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h3 className="text-lg font-semibold mb-4">集团其他业务</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {brandConfig.groupBusinesses.map((business) => (
                <a 
                  key={business.id}
                  href={business.url} 
                  className="text-gray-300 hover:text-white transition-colors duration-200 py-2 px-4 rounded-lg hover:bg-gray-700"
                  title={business.description}
                  onClick={(e) => e.preventDefault()}
                >
                  {business.name}
                </a>
              ))}
            </div>
          </div>
          
          {/* 分隔线 */}
          <div className="border-t border-gray-700 pt-6">
            <div className="text-center text-gray-400 text-sm">
              <p>&copy; {brandConfig.copyright.year} {brandConfig.copyright.company}. {brandConfig.copyright.text}</p>
              <p><a href="https://beian.miit.gov.cn/" target="_blank">皖ICP备2023010270号</a></p>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Purchase Modal */}
      <PurchaseModal 
        isOpen={isPurchaseModalOpen} 
        onClose={() => setIsPurchaseModalOpen(false)} 
      />
      
      {/* Demo Modal */}
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />
    </div>
  );
};

export default MainLayout;